import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useGetSelectableCareproviders } from "apollo/hooks/queries";
import {
  FILE_SHARE_MODE_NONE,
  FILE_SHARE_MODE_SELECTED,
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
  FILE_TABLE_ACTION_UPLOAD_AND_SHARE_HIS,
  QUERY_PROGRESS_PENDING,
  TRACK_EVENTS,
} from "core/consts";
import { isFileHISStateful } from "core/model/files/utils";
import { activateSelectableProviders } from "core/model/utils/featureFlags";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntologiesForSelect } from "core/model/utils/ontologies/hooks";
import { Filev2, QueryProgress, RadioOption } from "core/types";
import RadioGroup from "ds_legacy/components/RadioGroup";
import RSButton from "ds_legacy/components/RSButton";
import { SelectConnectedWithForm } from "ds_legacy/components/Select";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { Divider } from "ds_legacy/materials/layouts";
import { margin, padding } from "ds_legacy/materials/metrics";
import { Caption } from "ds_legacy/materials/typography";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { getFileSource, isFileForm } from "dsl/ecosystems/PatientFiles/utils";
import { useRef } from "react";
import {
  FormWatcher,
  SimpleFormRenderProps,
  convertModelDefinition,
  multiReactSelectDef,
  valueDef,
} from "react-forms-state";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import {
  FileShareModalProps,
  FileShareResult,
  FileShareResultWithInitialValues,
  FileShareValidationResult,
} from ".";
import { useDialogStyles } from "..";
import { getFormInputValue } from "./utils";

const fileShareModelDefinition = {
  ...valueDef("share_mode", { fieldRequired: true }),
  ...multiReactSelectDef("selected_receivers", {
    convertIn: (value: RadioOption[]) => ({ value }),
    mapToValuesArray: false,
  }),
};

const validateFileShareInput = ({
  selected_receivers,
  share_mode,
}: FileShareValidationResult) => {
  if (
    share_mode === FILE_SHARE_MODE_SELECTED &&
    !selected_receivers?.value?.length
  ) {
    return { selected_receivers: false };
  }
  return true;
};

function getShareModeForTracking(share_mode: Filev2["share_mode"]) {
  if (share_mode === 1) return "all_potential_receivers";
  if (share_mode === 2) return "selected_receivers";
  return "no_one";
}

export function ShareFile({
  closeModal,
  file,
  handleShareFile,
  mutationProgress,
  setAction,
}: {
  closeModal: FileShareModalProps["reset"];
  file: FileShareModalProps["file"];
  handleShareFile: (file: FileShareResultWithInitialValues) => Promise<void>;
  mutationProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
}) {
  const classes = useDialogStyles();
  const translations = useTranslations();
  const { trackEvent } = useTracking();

  const isHISFile = isFileHISStateful(file);
  const initialValues = getFormInputValue({
    share_mode: isHISFile ? FILE_SHARE_MODE_NONE : file?.share_mode,
    shared_with: isHISFile ? undefined : file?.shared_with,
  });
  const initialValuesRef = useRef(initialValues);

  const [queryProgress, selectableCareproviderOptions = []] =
    useGetSelectableCareproviders({
      fileId: file?.id ?? -1,
      skip: file?.share_mode !== FILE_SHARE_MODE_SELECTED,
    });

  function getFileShareOptionsDisabled(options: RadioOption) {
    if (options.value === FILE_SHARE_MODE_SELECTED) {
      if (
        queryProgress !== QUERY_PROGRESS_PENDING &&
        !selectableCareproviderOptions?.length
      ) {
        return true;
      }
      if (!activateSelectableProviders()) {
        return true;
      }
    }
    return false;
  }

  const fileShareOptions = useGetOntologiesForSelect({
    type: ONTOLOGIES.FILE_SHARE_MODE,
  }).map((options) => ({
    ...options,
    disabled: getFileShareOptionsDisabled(options),
  }));

  async function handleSubmit(data: FileShareResult) {
    if (isFileHISStateful(file)) {
      if (data.share_mode === FILE_SHARE_MODE_NONE) {
        return closeModal();
      }

      return setAction({
        data: [{ ...file, share_mode: data.share_mode }],
        type: FILE_TABLE_ACTION_UPLOAD_AND_SHARE_HIS,
      });
    }

    if (isFileForm(file)) {
      if (data.share_mode === FILE_SHARE_MODE_NONE) {
        return closeModal();
      }
      return setAction({
        data: { ...file, share_mode: data.share_mode },
        type: FILE_TABLE_ACTION_UPLOAD_AND_SHARE_FORM,
      });
    }

    return handleShareFile({
      ...data,
      initialValues: initialValuesRef.current,
    });
  }

  return (
    <SimpleFormRenderProps
      key={file?.id}
      formInputValue={initialValues}
      modelDefinition={convertModelDefinition(fileShareModelDefinition)}
      validate={validateFileShareInput}
      onSubmit={handleSubmit}
    >
      {({ submit, valueChangeObs }) => {
        return (
          <div style={{ padding: padding(1, 1, 1, 0) }}>
            <DialogTitle style={{ paddingBottom: 0 }}>
              {
                translations.fileSection.fileShareComponent
                  .shareWithReceiverTitle
              }
            </DialogTitle>
            <DialogContent classes={{ root: classes.dialogContent }}>
              <Caption style={{ margin: 0, whiteSpace: "normal" }}>
                {
                  translations.fileSection.fileShareComponent.shareOptions
                    .shareOptionsLegalDisclaimer
                }
              </Caption>
              <Divider margin={margin(2, 0)} />
              <RadioGroup
                elementName="share_mode"
                isAlwaysChecked
                label="" // #TODO add missing label
                options={fileShareOptions}
                showOnlyActive
              />
              <FormWatcher watchPath="share_mode">
                {({ watchedValue }) =>
                  watchedValue === FILE_SHARE_MODE_SELECTED ? (
                    <>
                      <SelectConnectedWithForm
                        disabled={
                          !selectableCareproviderOptions?.length ||
                          !activateSelectableProviders()
                        }
                        elementName="selected_receivers"
                        isLoading={queryProgress === QUERY_PROGRESS_PENDING}
                        minWidth="100%"
                        multiple
                        options={selectableCareproviderOptions.map(
                          ({ id, name }) => ({ id, label: name, value: id }),
                        )}
                        placeholder={
                          translations.general.selectDefaultPlaceholder
                        }
                        value={initialValuesRef.current.selected_receivers}
                      />
                    </>
                  ) : null
                }
              </FormWatcher>
            </DialogContent>
            <DialogActions>
              <RSButton
                color="primary"
                disabled={mutationProgress === QUERY_PROGRESS_PENDING}
                id="back"
                loading="na"
                variant="text"
                onClick={() => {
                  trackEvent({
                    cta: "cancel",
                    file_implementation: "HIS",
                    modal: "share",
                    name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
                    source: getFileSource(file),
                  });
                  closeModal();
                }}
              >
                {translations.actions.cancel}
              </RSButton>
              <RSButton
                color="primary"
                id="share"
                inverted
                loading={mutationProgress}
                variant="text"
                onClick={() => {
                  submit();
                  valueChangeObs
                    .subscribe(({ value }) => {
                      trackEvent({
                        cta: "share",
                        file_implementation: "HIS",
                        modal: "share",
                        name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
                        shared: getShareModeForTracking(value.share_mode),
                        source: getFileSource(file),
                      });
                    })
                    .unsubscribe();
                }}
              >
                {translations.actions.shareFile}
              </RSButton>
            </DialogActions>
          </div>
        );
      }}
    </SimpleFormRenderProps>
  );
}
